import React, { useMemo, useState } from 'react';
import Modal from './Modal';
import { Stock, AllergenInfo } from '../types';
import { apiService } from '../services/api';
import { AllergenManager } from './AllergenManager';
import './ProductStockSelector.css';

interface ProductStockSelectorProps {
    stocks: Stock[];
    selectedStockIds: string[];
    stockQuantityOverrides?: Record<string, number>;
    onToggleStock: (stockId: string) => void;
    onOverrideChange?: (stockId: string, quantity: number) => void;
    loading?: boolean;
    disabled?: boolean;
    onStocksChanged?: () => Promise<void> | void;
    modalTitle?: string;
    modalSubtitle?: string;
    enableQuantityOverrides?: boolean;
}

const ProductStockSelector: React.FC<ProductStockSelectorProps> = ({
    stocks,
    selectedStockIds,
    stockQuantityOverrides = {},
    onToggleStock,
    onOverrideChange,
    loading = false,
    disabled = false,
    onStocksChanged,
    modalTitle = 'Manage stock links',
    modalSubtitle = 'Select the stocks this product consumes and optionally override their quantities.',
    enableQuantityOverrides = true,
}) => {
    const [isModalOpen, setModalOpen] = useState(false);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [showInactive, setShowInactive] = useState(false);
    const [createForm, setCreateForm] = useState<{
        name: string;
        quantity: string;
        unit: string;
        notes: string;
        allergens: AllergenInfo[];
    }>({
        name: '',
        quantity: '',
        unit: '',
        notes: '',
        allergens: [],
    });
    const [createError, setCreateError] = useState<string | null>(null);
    const [createSubmitting, setCreateSubmitting] = useState(false);
    const [editingStockId, setEditingStockId] = useState<string | null>(null);
    const [editForm, setEditForm] = useState<{
        name: string;
        quantity: string;
        unit: string;
        notes: string;
        allergens: AllergenInfo[];
    } | null>(null);
    const [editError, setEditError] = useState<string | null>(null);
    const [editSubmitting, setEditSubmitting] = useState(false);

    const stockMap = useMemo(() => new Map(stocks.map((stock): [string, Stock] => [stock.id, stock])), [stocks]);

    const filteredStocks = useMemo(() => {
        if (showInactive) {
            return stocks;
        }
        return stocks.filter(stock => stock.isActive === true);
    }, [stocks, showInactive]);

    const selectedStocks = useMemo(() => {
        return selectedStockIds
            .map((id) => stockMap.get(id))
            .filter((stock): stock is Stock => Boolean(stock));
    }, [selectedStockIds, stockMap]);

    const hasStocks = filteredStocks.length > 0;

    const renderAvailability = (stock: Stock) => {
        if (stock.quantity <= 0) {
            return {
                className: 'badge out',
                label: 'Out of stock',
            };
        }

        if (stock.quantity <= 5) {
            return {
                className: 'badge low',
                label: `Low (${stock.quantity})`,
            };
        }

        return {
            className: 'badge good',
            label: `In stock (${stock.quantity})`,
        };
    };

    const renderOverrideValue = (stock: Stock) => {
        if (!enableQuantityOverrides) {
            return null;
        }

        const override = stockQuantityOverrides[stock.id];
        if (override === undefined || override === stock.quantity) {
            return null;
        }

        return (
            <span className="override-value">Override: {override}</span>
        );
    };

    const resetCreateForm = () => {
        setCreateForm({
            name: '',
            quantity: '',
            unit: '',
            notes: '',
            allergens: [],
        });
        setCreateError(null);
    };

    const closeModal = () => {
        setModalOpen(false);
        setShowCreateForm(false);
        setEditingStockId(null);
        setEditForm(null);
        resetCreateForm();
    };

    const startEditingStock = (stock: Stock) => {
        setEditingStockId(stock.id);
        setEditForm({
            name: stock.name,
            quantity: stock.quantity.toString(),
            unit: stock.unit || '',
            notes: stock.notes || '',
            allergens: stock.allergens || [],
        });
        setEditError(null);
        setShowCreateForm(false);
    };

    const cancelEditingStock = () => {
        setEditingStockId(null);
        setEditForm(null);
        setEditError(null);
    };

    const handleUpdateStock = async (event: React.FormEvent) => {
        event.preventDefault();
        if (disabled || !editingStockId || !editForm) {
            return;
        }

        const trimmedName = editForm.name.trim();
        if (!trimmedName) {
            setEditError('Stock name is required');
            return;
        }

        const parsedQuantity = Math.max(0, Math.floor(Number(editForm.quantity || '0')));

        setEditSubmitting(true);
        setEditError(null);

        try {
            await apiService.updateStock(editingStockId, {
                name: trimmedName,
                quantity: parsedQuantity,
                unit: editForm.unit.trim() || undefined,
                notes: editForm.notes.trim() || undefined,
                allergens: editForm.allergens.length > 0 ? editForm.allergens : undefined,
            });

            // Exit edit mode first - the stock will appear in the list in read-only mode
            cancelEditingStock();

            // Refresh stocks after UI updates to avoid modal closing during re-render
            if (onStocksChanged) {
                await onStocksChanged();
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to update stock';
            setEditError(message);
        } finally {
            setEditSubmitting(false);
        }
    };

    const openModal = () => {
        resetCreateForm();
        setShowCreateForm(false);
        setModalOpen(true);
    };

    const handleCreateStock = async (event: React.FormEvent) => {
        event.preventDefault();
        if (disabled) {
            return;
        }

        const trimmedName = createForm.name.trim();
        if (!trimmedName) {
            setCreateError('Stock name is required');
            return;
        }

        const parsedQuantity = Math.max(0, Math.floor(Number(createForm.quantity || '0')));

        setCreateSubmitting(true);
        setCreateError(null);

        try {
            const newStock = await apiService.createStock({
                name: trimmedName,
                quantity: parsedQuantity,
                unit: createForm.unit.trim() || undefined,
                notes: createForm.notes.trim() || undefined,
                allergens: createForm.allergens.length > 0 ? createForm.allergens : undefined,
            });

            onToggleStock(newStock.id);
            onOverrideChange?.(newStock.id, newStock.quantity);

            // Hide the create form first, don't close the modal
            setShowCreateForm(false);
            resetCreateForm();

            // Refresh stocks after UI updates to avoid modal closing during re-render
            if (onStocksChanged) {
                await onStocksChanged();
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to create stock';
            setCreateError(message);
        } finally {
            setCreateSubmitting(false);
        }
    };

    return (
        <div className="product-stock-selector">
            <div className="selector-toolbar">
                <button
                    type="button"
                    className="edit-stocks-btn"
                    onClick={openModal}
                    disabled={disabled}
                >
                    {hasStocks ? 'Edit stock links' : 'Create stock items'}
                </button>

                {loading ? (
                    <span className="info-text">Loading stock records…</span>
                ) : selectedStocks.length > 0 ? (
                    <div className="selected-chip-row">
                        {selectedStocks.map((stock) => {
                            const availability = renderAvailability(stock);
                            const toneClass = availability.className.includes('out')
                                ? 'chip-out'
                                : availability.className.includes('low')
                                    ? 'chip-low'
                                    : 'chip-good';
                            return (
                                <span key={stock.id} className={`stock-chip ${toneClass}`}>
                                    <span className="chip-name">{stock.name}</span>
                                    {stock.unit && <span className="chip-unit">· {stock.unit}</span>}
                                    {renderOverrideValue(stock)}
                                    <span className="chip-badge">{availability.label}</span>
                                </span>
                            );
                        })}
                    </div>
                ) : (
                    <span className="empty-state inline">No stock items linked yet.</span>
                )}
            </div>

            <Modal
                isOpen={isModalOpen}
                onClose={closeModal}
                ariaLabelledBy="product-stock-modal-title"
                className="product-stock-modal"
            >
                <div className="modal-header">
                    <h2 id="product-stock-modal-title">{modalTitle}</h2>
                    <p className="modal-subtitle">{modalSubtitle}</p>
                </div>

                <div className="stock-filter-section">
                    <label className="filter-checkbox">
                        <input
                            type="checkbox"
                            checked={showInactive}
                            onChange={(e) => setShowInactive(e.target.checked)}
                        />
                        <span>Show Inactive Items</span>
                    </label>
                </div>

                <div className="modal-toolbar">
                    <button
                        type="button"
                        className="create-stock-btn"
                        onClick={() => {
                            if (disabled) {
                                return;
                            }
                            setShowCreateForm((prev) => {
                                const next = !prev;
                                if (!next) {
                                    resetCreateForm();
                                }
                                return next;
                            });
                        }}
                        disabled={disabled}
                    >
                        {showCreateForm ? 'Cancel new stock' : 'New stock item'}
                    </button>
                </div>

                {showCreateForm && (
                    <form className="create-stock-form" onSubmit={handleCreateStock}>
                        <div className="create-stock-grid">
                            <label className="create-field">
                                <span>Name *</span>
                                <input
                                    type="text"
                                    value={createForm.name}
                                    onChange={(event) => setCreateForm((prev) => ({ ...prev, name: event.target.value }))}
                                    disabled={createSubmitting || disabled}
                                    placeholder="e.g., Chicken Portions"
                                    required
                                />
                            </label>
                            <label className="create-field">
                                <span>Quantity *</span>
                                <input
                                    type="number"
                                    min={0}
                                    step={1}
                                    value={createForm.quantity}
                                    onChange={(event) => setCreateForm((prev) => ({ ...prev, quantity: event.target.value }))}
                                    disabled={createSubmitting || disabled}
                                    required
                                />
                            </label>
                        </div>
                        <div className="create-stock-grid">
                            <label className="create-field">
                                <span>Unit</span>
                                <input
                                    type="text"
                                    value={createForm.unit}
                                    onChange={(event) => setCreateForm((prev) => ({ ...prev, unit: event.target.value }))}
                                    disabled={createSubmitting || disabled}
                                    placeholder="e.g., portions"
                                />
                            </label>
                            <label className="create-field">
                                <span>Notes</span>
                                <input
                                    type="text"
                                    value={createForm.notes}
                                    onChange={(event) => setCreateForm((prev) => ({ ...prev, notes: event.target.value }))}
                                    disabled={createSubmitting || disabled}
                                    placeholder="Optional"
                                />
                            </label>
                        </div>
                        <label className="create-field">
                            <span>Allergens</span>
                            <AllergenManager
                                allergens={createForm.allergens}
                                onChange={(allergens) => setCreateForm(prev => ({ ...prev, allergens }))}
                                disabled={createSubmitting || disabled}
                            />
                        </label>
                        {createError && <p className="create-error">{createError}</p>}
                        <div className="create-actions">
                            <button
                                type="submit"
                                className="submit-create-btn"
                                disabled={createSubmitting || disabled}
                            >
                                {createSubmitting ? 'Creating…' : 'Create & link'}
                            </button>
                        </div>
                    </form>
                )}

                {loading ? (
                    <p className="info-text">Loading stock records…</p>
                ) : !hasStocks ? (
                    <div className="empty-state">No stock items available. Create stock items first, then link them.</div>
                ) : (
                    <div className="stock-list-panel">
                        {filteredStocks.map((stock) => {
                            const isSelected = selectedStockIds.includes(stock.id);
                            const overrideQuantity = stockQuantityOverrides[stock.id] ?? stock.quantity;
                            const availability = renderAvailability(stock);
                            const isEditing = editingStockId === stock.id;

                            if (isEditing && editForm) {
                                return (
                                    <form key={stock.id} className="stock-edit-form" onSubmit={handleUpdateStock}>
                                        <div className="edit-stock-grid">
                                            <label className="edit-field">
                                                <span>Name *</span>
                                                <input
                                                    type="text"
                                                    value={editForm.name}
                                                    onChange={(e) => setEditForm(prev => prev ? { ...prev, name: e.target.value } : null)}
                                                    disabled={editSubmitting || disabled}
                                                    required
                                                />
                                            </label>
                                            <label className="edit-field">
                                                <span>Quantity *</span>
                                                <input
                                                    type="number"
                                                    min={0}
                                                    step={1}
                                                    value={editForm.quantity}
                                                    onChange={(e) => setEditForm(prev => prev ? { ...prev, quantity: e.target.value } : null)}
                                                    disabled={editSubmitting || disabled}
                                                    required
                                                />
                                            </label>
                                        </div>
                                        <div className="edit-stock-grid">
                                            <label className="edit-field">
                                                <span>Unit</span>
                                                <input
                                                    type="text"
                                                    value={editForm.unit}
                                                    onChange={(e) => setEditForm(prev => prev ? { ...prev, unit: e.target.value } : null)}
                                                    disabled={editSubmitting || disabled}
                                                />
                                            </label>
                                            <label className="edit-field">
                                                <span>Notes</span>
                                                <input
                                                    type="text"
                                                    value={editForm.notes}
                                                    onChange={(e) => setEditForm(prev => prev ? { ...prev, notes: e.target.value } : null)}
                                                    disabled={editSubmitting || disabled}
                                                />
                                            </label>
                                        </div>
                                        <label className="edit-field">
                                            <span>Allergens</span>
                                            <AllergenManager
                                                allergens={editForm.allergens}
                                                onChange={(allergens) => setEditForm(prev => prev ? { ...prev, allergens } : null)}
                                                disabled={editSubmitting || disabled}
                                            />
                                        </label>
                                        {editError && <p className="edit-error">{editError}</p>}
                                        <div className="edit-actions">
                                            <button type="button" onClick={cancelEditingStock} disabled={editSubmitting || disabled}>
                                                Cancel
                                            </button>
                                            <button type="submit" className="submit-edit-btn" disabled={editSubmitting || disabled}>
                                                {editSubmitting ? 'Saving…' : 'Save'}
                                            </button>
                                        </div>
                                    </form>
                                );
                            }

                            return (
                                <div
                                    key={stock.id}
                                    className={`stock-row ${isSelected ? 'selected' : ''}`}
                                >
                                    <label className="stock-row-main">
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => onToggleStock(stock.id)}
                                            disabled={disabled}
                                        />
                                        <div className="stock-row-content">
                                            <span className="stock-name">{stock.name}</span>
                                            <span className={availability.className}>{availability.label}</span>
                                            {stock.unit && <span className="stock-unit">Unit: {stock.unit}</span>}
                                            {stock.notes && <span className="stock-notes">{stock.notes}</span>}
                                            {stock.allergens && stock.allergens.length > 0 && (
                                                <span className="stock-allergens">
                                                    Allergens: {stock.allergens.map(a => a.allergen).join(', ')}
                                                </span>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            className="edit-stock-btn"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                startEditingStock(stock);
                                            }}
                                            disabled={disabled}
                                        >
                                            Edit
                                        </button>
                                    </label>
                                    {isSelected && enableQuantityOverrides && (
                                        <div className="stock-row-override">
                                            <label htmlFor={`stock-override-${stock.id}`}>Override quantity</label>
                                            <input
                                                id={`stock-override-${stock.id}`}
                                                type="number"
                                                min={0}
                                                step={1}
                                                value={overrideQuantity}
                                                onChange={(event) => onOverrideChange?.(stock.id, Number(event.target.value) || 0)}
                                                disabled={disabled}
                                            />
                                            <p className="override-help">New quantity is applied once you save the product.</p>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                <div className="modal-actions">
                    <button
                        type="button"
                        className="close-modal-btn"
                        onClick={closeModal}
                    >
                        Done
                    </button>
                </div>
            </Modal>
        </div>
    );
};

export default ProductStockSelector;
